import React, { useEffect, useState } from "react";

import Draggable from "react-draggable";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { toast } from "react-toastify";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useOsCatalogs from "../../hooks/useOsCatalogs";

const PaperComponent = props => (
  <Draggable handle="#create-os-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
    <Paper {...props} />
  </Draggable>
);

const CreateOsModal = ({ modalOpen, onClose, ticket, initialSuggestion }) => {
	const ticketId = ticket?.id;
	const ticketContactId = ticket?.contact?.id;
	const [clientes, setClientes] = useState([]);
	const [cliente, setCliente] = useState(null);
	const [objetos, setObjetos] = useState([]);
	const [objeto, setObjeto] = useState(null);
	const [objetosPadrao, setObjetosPadrao] = useState([]);
	const [objetoPadrao, setObjetoPadrao] = useState(null);
	const [prioridade, setPrioridade] = useState(null);
	const { data: sharedCatalogs } = useOsCatalogs();
	const [searchParam, setSearchParam] = useState("");
	const [loadingClientes, setLoadingClientes] = useState(false);
	const [loadingObjetos, setLoadingObjetos] = useState(false);
	const [loadingObjetosPadrao, setLoadingObjetosPadrao] = useState(false);
	const [saving, setSaving] = useState(false);
	const [descricaoCurta, setDescricaoCurta] = useState("");
	const [descricao, setDescricao] = useState("");
	const [vinculoContato, setVinculoContato] = useState({
		terceiroId: null,
		contatoId: null,
	});

	useEffect(() => {
		if (!modalOpen) return;

		// Default legado do fluxo reduzido: prioridade 1.
		const defaultPriority =
			(sharedCatalogs?.prioridades || []).find(
				item => String(item.id) === "1"
			) || null;
		setPrioridade(previous => previous || defaultPriority);
	}, [modalOpen, sharedCatalogs]);

	useEffect(() => {
		if (!modalOpen || searchParam.length < 3) {
			setLoadingClientes(false);
			return;
		}

		setLoadingClientes(true);
		const delayDebounceFn = setTimeout(() => {
			const fetchClientes = async () => {
				try {
					const { data } = await api.get("/os/clientes", {
						params: { q: searchParam, per_page: 10 },
					});
					setClientes(data.data || []);
				} catch (err) {
					toastError(err);
				} finally {
					setLoadingClientes(false);
				}
			};

			fetchClientes();
		}, 500);

		return () => clearTimeout(delayDebounceFn);
	}, [searchParam, modalOpen]);

	useEffect(() => {
		if (!modalOpen) return;

		setLoadingObjetos(true);
		const fetchObjetos = async () => {
			try {
				const { data } = await api.get("/os/objetos", {
					params: { per_page: 200 },
				});
				setObjetos(data.data || []);
			} catch (err) {
				toastError(err);
			} finally {
				setLoadingObjetos(false);
			}
		};

		fetchObjetos();
	}, [modalOpen]);

	useEffect(() => {
		setObjetoPadrao(null);
		setObjetosPadrao([]);

		if (!modalOpen || !objeto?.id) return;

		setLoadingObjetosPadrao(true);
		const fetchObjetosPadrao = async () => {
			try {
				const { data } = await api.get("/os/objetos-padrao", {
					params: { objeto_id: objeto.id, per_page: 200 },
				});
				setObjetosPadrao(data.data || []);
			} catch (err) {
				toastError(err);
			} finally {
				setLoadingObjetosPadrao(false);
			}
		};

		fetchObjetosPadrao();
	}, [modalOpen, objeto]);

	useEffect(() => {
		if (!modalOpen) return;

		setLoadingObjetos(true);
		const fetchObjetos = async () => {
			try {
				const { data } = await api.get("/os/objetos", {
					params: { grupo_id: 1, per_page: 200 },
				});
				setObjetos(data.data || []);
			} catch (err) {
				toastError(err);
			} finally {
				setLoadingObjetos(false);
			}
		};

		fetchObjetos();
	}, [modalOpen]);

	useEffect(() => {
		setObjetoPadrao(null);
		setObjetosPadrao([]);

		if (!modalOpen || !objeto?.id) return;

		setLoadingObjetosPadrao(true);
		const fetchObjetosPadrao = async () => {
			try {
				const { data } = await api.get("/os/objetos-padrao", {
					params: { objeto_id: objeto.id, per_page: 200 },
				});
				setObjetosPadrao(data.data || []);
			} catch (err) {
				toastError(err);
			} finally {
				setLoadingObjetosPadrao(false);
			}
		};

		fetchObjetosPadrao();
	}, [modalOpen, objeto]);

	useEffect(() => {
		if (!modalOpen || !ticketId || !ticketContactId) return;

		setDescricaoCurta(initialSuggestion?.descricao_curta || "");
		setDescricao(initialSuggestion?.descricao || "");
		let active = true;

		const fetchVinculoAtual = async () => {
			setLoadingClientes(true);
			try {
				const { data: contactResponse } = await api.get(
					`/contacts/${ticketContactId}`
				);
				const contatoAtual = contactResponse.data || contactResponse;
				const osTerceiroId = contatoAtual.osTerceiroId;

				if (!active) return;

				setVinculoContato({
					terceiroId: osTerceiroId || null,
					contatoId: contatoAtual.osContatoId || null,
				});

				const clienteId = osTerceiroId || initialSuggestion?.cliente_id;
				if (!clienteId) return;

				const { data } = await api.get(`/os/clientes/${clienteId}`);
				const clienteVinculado = data.data || data;

				if (active) {
					setClientes([clienteVinculado]);
					setCliente(clienteVinculado);
				}
			} catch (err) {
				if (active) toastError(err);
			} finally {
				if (active) setLoadingClientes(false);
			}
		};

		fetchVinculoAtual();

		return () => {
			active = false;
		};
	}, [modalOpen, ticketId, ticketContactId, initialSuggestion]);

	useEffect(() => {
		if (!modalOpen || !initialSuggestion) return;
		setDescricaoCurta(initialSuggestion.descricao_curta || "");
		setDescricao(initialSuggestion.descricao || "");
	}, [modalOpen, initialSuggestion]);

	useEffect(() => {
		if (!modalOpen || !initialSuggestion?.objeto_id || !objetos.length) return;
		const selected = objetos.find(item => String(item.id) === String(initialSuggestion.objeto_id));
		if (selected) setObjeto(selected);
	}, [modalOpen, initialSuggestion, objetos]);

	useEffect(() => {
		if (!modalOpen || !initialSuggestion?.objeto_padrao_id || !objetosPadrao.length) return;
		const selected = objetosPadrao.find(item => String(item.id) === String(initialSuggestion.objeto_padrao_id));
		if (selected) setObjetoPadrao(selected);
	}, [modalOpen, initialSuggestion, objetosPadrao]);

	const handleClose = () => {
		if (saving) return;

		setClientes([]);
		setCliente(null);
		setObjetos([]);
		setObjeto(null);
		setObjetosPadrao([]);
		setObjetoPadrao(null);
		setPrioridade(null);
		setSearchParam("");
		setDescricaoCurta("");
		setDescricao("");
		setVinculoContato({ terceiroId: null, contatoId: null });
		onClose();
	};

	const handleCreateOs = async () => {
		if (!cliente) {
			toast.error("Selecione o cliente da OS");
			return;
		}

		if (!objeto) {
			toast.error("Selecione o objeto da OS");
			return;
		}

		if (!objetoPadrao) {
			toast.error("Selecione o objeto padrão da OS");
			return;
		}

		if (!descricaoCurta.trim()) {
			toast.error("Informe a descrição curta");
			return;
		}

		if (!descricao.trim()) {
			toast.error("Informe a descrição");
			return;
		}

		setSaving(true);
		try {
			const { data } = await api.post(`/tickets/${ticket.id}/os`, {
				cliente_id: cliente.id,
				contato_id: String(cliente.id) === String(vinculoContato.terceiroId)
					? vinculoContato.contatoId || undefined
					: undefined,
				objeto_id: objeto.id,
				objeto_padrao_id: objetoPadrao.id,
				...(prioridade ? { prioridade_id: prioridade.id } : {}),
				descricao_curta: descricaoCurta,
				descricao: [
					`Ticket: #${ticket.id}`,
					`Contato: ${ticket.contact?.name || ""}`,
					`Telefone: ${ticket.contact?.number || ""}`,
					"",
					descricao,
				].join("\n"),
			});

			const osId = data?.data?.id || data?.id;
			toast.success(osId ? `OS ${osId} criada com sucesso` : "OS criada com sucesso");
			window.dispatchEvent(new CustomEvent("ticket-os-links-updated", { detail: { ticketId: ticket.id } }));
			handleClose();
		} catch (err) {
			toastError(err);
		} finally {
			setSaving(false);
		}
	};

	const renderClienteLabel = option => {
		if (!option) return "";
		const nomePrincipal = option.razao || option.nome || option.fantasia || "Cliente";
		const fantasia =
			option.fantasia &&
			option.fantasia.trim().toLocaleLowerCase() !== nomePrincipal.trim().toLocaleLowerCase()
				? ` · ${option.fantasia}`
				: "";
		const documento = option.cnpj_cpf ? ` - ${option.cnpj_cpf}` : "";

		return `${nomePrincipal}${fantasia}${documento}`;
	};

	const renderCadastroLabel = option => {
		if (!option) return "";
		return option.nome || "";
	};

	return (
		<Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" fullWidth PaperComponent={PaperComponent}>
			<DialogTitle id="create-os-dialog-title" style={{ cursor: "move" }}>
				Criar OS
			</DialogTitle>
			<DialogContent dividers>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Autocomplete
							fullWidth
							options={clientes}
							loading={loadingClientes}
							value={cliente}
							onChange={(event, newValue) => setCliente(newValue)}
							onInputChange={(event, value, reason) => {
								if (reason === "input" || reason === "clear") {
									setSearchParam(value);
								}
							}}
							filterOptions={options => options}
							getOptionLabel={renderClienteLabel}
							renderInput={params => (
								<TextField
									{...params}
									label="Cliente da OS"
									variant="outlined"
									autoFocus
									helperText="Busque por razão social, fantasia ou CNPJ/CPF"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{loadingClientes ? <CircularProgress color="inherit" size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Autocomplete
							fullWidth
							options={objetos}
							loading={loadingObjetos}
							value={objeto}
							onChange={(event, newValue) => setObjeto(newValue)}
							getOptionLabel={renderCadastroLabel}
							renderInput={params => (
								<TextField
									{...params}
									label="Objeto da OS"
									variant="outlined"
									required
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{loadingObjetos ? <CircularProgress color="inherit" size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Autocomplete
							fullWidth
							options={objetosPadrao}
							loading={loadingObjetosPadrao}
							value={objetoPadrao}
							onChange={(event, newValue) => setObjetoPadrao(newValue)}
							getOptionLabel={renderCadastroLabel}
							disabled={!objeto}
							renderInput={params => (
								<TextField
									{...params}
									label="Objeto padrão da OS"
									variant="outlined"
									required
									helperText={!objeto ? "Selecione um objeto primeiro" : ""}
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{loadingObjetosPadrao ? <CircularProgress color="inherit" size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<Autocomplete
							fullWidth
							options={sharedCatalogs?.prioridades || []}
							value={prioridade}
							onChange={(event, newValue) => setPrioridade(newValue)}
							getOptionLabel={renderCadastroLabel}
							renderInput={params => (
								<TextField
									{...params}
									label="Prioridade"
									variant="outlined"
									helperText="Padrão: prioridade 1 do sistema"
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							label="Descrição curta"
							variant="outlined"
							fullWidth
							value={descricaoCurta}
							required
							onChange={e => setDescricaoCurta(e.target.value)}
							inputProps={{ maxLength: 254 }}
						/>
					</Grid>
					<Grid item xs={12}>
						<TextField
							label="Descrição"
							variant="outlined"
							fullWidth
							multiline
							minRows={8}
							value={descricao}
							required
							onChange={e => setDescricao(e.target.value)}
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="secondary" disabled={saving} variant="outlined">
					Cancelar
				</Button>
				<ButtonWithSpinner
					variant="contained"
					type="button"
					disabled={saving || !cliente || !objeto || !objetoPadrao}
					onClick={handleCreateOs}
					color="primary"
					loading={saving}
				>
					Criar OS
				</ButtonWithSpinner>
			</DialogActions>
		</Dialog>
	);
};

export default CreateOsModal;
